import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import playerjs from "player.js";
import { toast, Toaster } from "sonner";
import { Lock, Loader2, Play, MessageSquare, Send, Check, X, Clock, Download, AlertCircle, Film, ThumbsUp, ThumbsDown, RotateCcw, Search as SearchIcon, FolderSearch, Tags as TagsIcon, FileText, Image as ImageIcon, ExternalLink, Layers, Pencil, Highlighter, Trash2, Undo2, ChevronRight, FolderOpen, Scissors } from "lucide-react";
import ClipRequestTray, { TRAY_KEY, loadTray, saveTray } from "@/components/ClipRequestTray";
import ClipRequestMarker from "@/components/ClipRequestMarker";
import PdfAnnotationLayer, { PALETTE as DRAW_PALETTE } from "@/components/PdfAnnotationLayer";
import { maybeNudgeIntoView } from "@/lib/scrollUtils";
import { isRecentlyTagged } from "@/lib/recentlyTagged";
import { downloadBlob, buildAssetFilename, extForAsset } from "@/lib/downloadBlob";
import PdfViewer from "@/components/PdfViewer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_video-tagging-lab/artifacts/we05yb1x_WC%20Logo%202018%20Outlined_White.png";

function fmtDuration(sec) {
  if (sec === null || sec === undefined || isNaN(sec) || sec < 0) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Per-share thumbnail heal deduping (tab-lifetime). If a thumbnail 404s once, we
// silently POST /heal-thumbnail to re-sync Bunny's real filename, then swap in
// the fresh signed URL. Only fires once per (slug, asset) per tab.
const HEALED = new Set();
async function healShareThumb(slug, assetId, accessToken, imgEl) {
  const k = `${slug}:${assetId}`;
  if (HEALED.has(k)) return;
  HEALED.add(k);
  try {
    const { data } = await axios.post(
      `${API}/share/${slug}/assets/${assetId}/heal-thumbnail`,
      null,
      { params: accessToken ? { token: accessToken } : {} },
    );
    if (data?.thumbnail_url && imgEl) {
      imgEl.src = data.thumbnail_url;
      imgEl.style.display = "";
    }
  } catch { /* silent */ }
}

// Per-share comment delete tokens (kept in localStorage so the commenter can
// delete their own comments from the same browser without an account).
const DEL_TOK_KEY = (slug) => `wd_share_${slug}_dtok`;

function getDeleteTokens(slug) {
  try { return JSON.parse(localStorage.getItem(DEL_TOK_KEY(slug)) || "{}"); }
  catch { return {}; }
}
function saveDeleteToken(slug, commentId, tok) {
  const m = getDeleteTokens(slug);
  m[commentId] = tok;
  localStorage.setItem(DEL_TOK_KEY(slug), JSON.stringify(m));
}
function getDeleteToken(slug, commentId) {
  return getDeleteTokens(slug)[commentId];
}
function removeDeleteToken(slug, commentId) {
  const m = getDeleteTokens(slug);
  delete m[commentId];
  localStorage.setItem(DEL_TOK_KEY(slug), JSON.stringify(m));
}

export default function ShareView() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [needPassword, setNeedPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [accessToken, setAccessToken] = useState(() => sessionStorage.getItem(`share_${slug}_token`) || "");
  const [data, setData] = useState(null);
  const [activeAssetId, setActiveAssetId] = useState(null);
  const [lightboxId, setLightboxId] = useState(null);
  const reviewAnchorRef = useRef(null);
  // Iter 75 — Public clip-request tray. Persisted per-slug in localStorage
  // so a field-day session survives page refreshes, video navigation, and
  // breadcrumb pops. Submitting fires /share/{slug}/clip-requests and
  // clears the tray; see components/ClipRequestTray.jsx.
  const [clipTray, setClipTray] = useState(() => (slug ? loadTray(slug) : []));
  const [autoplayNext, setAutoplayNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [folderSearch, setFolderSearch] = useState("");
  const [tagFilter, setTagFilter] = useState(null); // client-side strict-scope tag chip
  // Iter 73 — Folder-tree navigation on the public share page. `drillFolderId`
  // tracks which sub-folder the recipient has drilled into (null = shared
  // root). Changes trigger a /data?folder_id=X refetch that re-scopes the
  // asset list, subfolder tiles, breadcrumbs, and tag chips.
  const [drillFolderId, setDrillFolderId] = useState(null);

  // Strict per-share tag namespace: chips are provided solely by the
  // backend (`share_data.tags`) which aggregates only tags on the
  // share's DIRECT assets — never subfolder-only tags. The previous
  // client-side `Array.from(new Set(...))` fallback over
  // `data.assets` could leak tags from deeper nested items the
  // recipient should not see, violating strict multi-tenant metadata
  // isolation. The fallback has been removed (Iter 79) — if the
  // backend returns no `tags`, the chip row is empty, period.
  const shareTags = useMemo(() => {
    if (!Array.isArray(data?.tags)) return [];
    // Count occurrences from data.assets for the chip counter, but the
    // *keys* come from the strict backend list.
    const counts = new Map();
    for (const a of data?.assets || []) {
      for (const t of a.tags || []) counts.set(t, (counts.get(t) || 0) + 1);
    }
    return data.tags.map((name) => ({ name, count: counts.get(name) || 0 }));
  }, [data?.tags, data?.assets]);

  // Iter 73 — Grid-visibility rules:
  //   • No search / no tag filter: show DIRECT children of the current
  //     folder only (keeps the folder-tree UX honest — descendants live
  //     behind the subfolder tiles). For non-folder shares (asset/collection),
  //     `current_folder_id` is null so we fall through to showing everything.
  //   • Search or tag filter active: flatten and show every match across the
  //     current folder's subtree (the backend already scoped `assets` to that
  //     subtree when drillFolderId is set).
  const filteredFolderAssets = useMemo(() => {
    if (!data?.assets) return [];
    const q = folderSearch.trim().toLowerCase();
    const browsing = !q && !tagFilter;
    const currentFid = data.current_folder_id;
    return data.assets.filter((a) => {
      if (tagFilter && !(a.tags || []).includes(tagFilter)) return false;
      if (!q) {
        if (browsing && currentFid && a.folder_id && a.folder_id !== currentFid) return false;
        return true;
      }
      const hay = `${a.title || ""} ${a.description || ""} ${(a.tags || []).join(" ")} ${a.original_filename || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data?.assets, data?.current_folder_id, folderSearch, tagFilter]);

  // Auto-advance: when a collection-share video ends, jump to the next thumbnail and autoplay.
  const advanceToNext = () => {
    if (!data || data.kind !== "collection") return;
    const idx = data.assets.findIndex((a) => a.id === activeAssetId);
    if (idx < 0 || idx >= data.assets.length - 1) return; // last asset — stop
    setAutoplayNext(true);
    setActiveAssetId(data.assets[idx + 1].id);
  };

  // Load meta + try data
  useEffect(() => {
    axios.get(`${API}/share/${slug}/meta`)
      .then(({ data }) => {
        setMeta(data);
        if (data.expired) { setError("This link has expired."); setLoading(false); return; }
        if (data.has_password && !accessToken) { setNeedPassword(true); setLoading(false); return; }
        return loadData(accessToken);
      })
      .catch((e) => {
        setError(e.response?.status === 404 ? "Link not found." : "Unable to load share.");
        setLoading(false);
      });
    // eslint-disable-next-line
  }, [slug]);

  const loadData = async (tok, fid = null) => {
    setLoading(true);
    try {
      const params = {};
      if (tok) params.token = tok;
      if (fid) params.folder_id = fid;
      const { data } = await axios.get(`${API}/share/${slug}/data`, { params });
      setData(data);
      // Iter 73 — Auto-select the first asset only when there are no
      // subfolder tiles to browse (leaf view). This applies both at the
      // shared root AND when the recipient has drilled into a leaf folder.
      // Otherwise we keep activeAssetId=null so the tiles are the first
      // thing the recipient sees.
      const hasSubfolders = (data.subfolders?.length || 0) > 0;
      // Photos in a multi-item grid open in the lightbox on click instead.
      const first = data.assets?.[0];
      if (!hasSubfolders && first && !(first.kind === "photo" && data.assets.length > 1)) setActiveAssetId(first.id);
      else setActiveAssetId(null);
      // Reset tag filter + search when navigating folders — chips are
      // re-sourced from the new folder's direct tags.
      setTagFilter(null);
      setFolderSearch("");
      setNeedPassword(false);
    } catch (e) {
      if (e.response?.status === 401) setNeedPassword(true);
      else setError("Could not load share content.");
    } finally {
      setLoading(false);
    }
  };

  // Iter 74 — If the currently-active asset falls out of the filtered
  // results (user cleared search, applied a tag chip, drilled folders),
  // drop the player so the recipient returns to the default folder view.
  // The idle auto-select rule in loadData() handles leaf folders where
  // a single asset should auto-play.
  useEffect(() => {
    if (!activeAssetId) return;
    if (!data?.assets?.length) return;
    const stillIn = filteredFolderAssets.some((a) => a.id === activeAssetId);
    if (!stillIn) setActiveAssetId(null);
  }, [filteredFolderAssets, activeAssetId, data?.assets]);

  // Iter 73 — Refetch when the recipient drills into / out of a subfolder.
  // Skip the first render — the mount effect above already fires the initial
  // /data request. After that, any change to `drillFolderId` triggers a
  // scoped refetch.
  const didMountDrill = useRef(false);
  useEffect(() => {
    if (!didMountDrill.current) { didMountDrill.current = true; return; }
    if (!meta || needPassword) return;
    loadData(accessToken, drillFolderId);
    // eslint-disable-next-line
  }, [drillFolderId]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/share/${slug}/access`, { password });
      sessionStorage.setItem(`share_${slug}_token`, data.token);
      setAccessToken(data.token);
      await loadData(data.token);
    } catch (ex) {
      setError(ex.response?.data?.detail || "Incorrect password");
    }
  };

  if (loading) {
    return <CenterScreen><Loader2 className="w-6 h-6 animate-spin text-[var(--brand-accent)]" /></CenterScreen>;
  }
  if (error) {
    return (
      <CenterScreen>
        <div className="text-center max-w-md">
          <AlertCircle className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <h1 className="font-heading text-xl font-semibold">{error}</h1>
          <p className="text-sm text-zinc-500 mt-2">Please check with whoever sent you this link.</p>
        </div>
      </CenterScreen>
    );
  }
  if (needPassword) {
    return (
      <CenterScreen>
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO} alt="Whiplash" className="h-12 w-auto" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-3">{meta?.title || "Whiplash"}</div>
          </div>
          <form onSubmit={submitPassword} className="bg-zinc-950/40 border border-zinc-800/60 rounded-xl p-7 backdrop-blur-sm space-y-4">
            <div>
              <div className="flex items-center gap-2 text-sm text-zinc-300 mb-1">
                <Lock className="w-4 h-4 text-[var(--brand-accent)]" />
                <span>Password protected</span>
              </div>
              <p className="text-xs text-zinc-500">Enter the password shared with you by {meta?.created_by_name || "the agency"}.</p>
            </div>
            <input
              autoFocus
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-black/40 border border-zinc-800 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60"
            />
            {error && <div className="text-xs text-red-400">{error}</div>}
            <button type="submit" className="w-full bg-[var(--brand-accent)] text-black font-semibold rounded-md py-2.5 text-sm hover:bg-[var(--brand-accent)]/90 transition">
              Continue
            </button>
          </form>
        </div>
      </CenterScreen>
    );
  }

  const activeAsset = data?.assets.find((a) => a.id === activeAssetId);
  const watchOnly = !!data && !data.allow_comments && !data.allow_approval;

  return (
    <div className="min-h-screen grain-overlay" style={{ overflowX: "clip" }}>
      <header className="sticky top-0 z-30 border-b border-zinc-900/80 bg-[#09090b]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <img src={LOGO} alt="Whiplash" className="h-6 sm:h-7 w-auto shrink-0" />
            <div className="w-px h-6 bg-zinc-800 shrink-0" />
            <div className="flex flex-col leading-tight min-w-0 flex-1">
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{watchOnly ? "Sharing" : "Review"}</span>
              <span className="text-xs sm:text-sm font-medium text-zinc-200 truncate">{data?.title}</span>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hidden sm:block shrink-0">
            From {data?.created_by_name}
          </div>
        </div>
      </header>

      <main data-testid="share-main" className={`mx-auto w-full min-w-0 px-3 sm:px-4 md:px-10 py-6 md:py-8 ${watchOnly ? "max-w-7xl lg:max-w-4xl" : "max-w-7xl"}`}>
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1 truncate">
            {watchOnly ? `Sharing: ${data?.title}` : "For your review"}
          </div>
          {/* Iter 73 — Folder-tree breadcrumbs. First crumb is always the
              shared root; clicks pop the recipient back up the tree. For
              non-folder shares the array is empty and we fall back to the
              plain title heading. */}
          {data?.kind === "folder" && (data?.breadcrumbs?.length || 0) > 0 ? (
            <>
              <nav
                data-testid="share-breadcrumbs"
                aria-label="Folder breadcrumb"
                className="flex items-center gap-1 flex-wrap text-xs sm:text-sm text-zinc-500 mb-1"
              >
                {data.breadcrumbs.map((b, i) => {
                  const isLast = i === data.breadcrumbs.length - 1;
                  const isRoot = i === 0;
                  return (
                    <span key={b.id} className="flex items-center gap-1">
                      {i > 0 && <ChevronRight className="w-3 h-3 text-zinc-700" />}
                      {isLast ? (
                        <span className="text-zinc-200 font-medium" data-testid={`crumb-current-${b.id}`}>{b.name}</span>
                      ) : (
                        <button
                          type="button"
                          data-testid={`crumb-${b.id}`}
                          onClick={() => setDrillFolderId(isRoot ? null : b.id)}
                          className="hover:text-[var(--brand-accent)] transition"
                        >
                          {b.name}
                        </button>
                      )}
                    </span>
                  );
                })}
              </nav>
              <h1 className="font-heading text-base sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight break-all leading-tight">
                {data.breadcrumbs[data.breadcrumbs.length - 1]?.name || data?.title}
              </h1>
            </>
          ) : (
            <h1 className="font-heading text-base sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight break-all leading-tight">{data?.title}</h1>
          )}
          <div className="text-xs sm:text-sm text-zinc-500 mt-1">
            {data?.assets.length || 0} {data?.assets.length === 1 ? "video" : "videos"}
          </div>
        </div>

        {/* Iter 73 — Subfolder tiles. Only rendered when there's at least
            one direct child AND the recipient isn't actively searching /
            filtering (search flattens the subtree, so tiles would be
            redundant). Click a tile to drill in; breadcrumbs let them
            climb back out. */}
        {(data?.kind === "folder" || data?.kind === "collection") && data.assets.length >= 1 && (
          <>
            {/* Iter 73b — Search bar is always rendered on folder /
                collection shares. Previous threshold (`assets.length >= 4`)
                hid it on small/empty parent folders — which is exactly the
                case where the recipient wants to search across subfolders.
                Scope is the current drill's subtree, enforced server-side.
                Iter 74 — rendered BEFORE subfolder tiles so the search is
                always the first interaction below the heading. */}
            {data.assets.length >= 1 && (() => {
              const recursive = data?.kind === "folder" && data?.include_subfolders;
              return (
                <div className="mb-4">
                  <div className={`relative ${recursive ? "max-w-xl" : "max-w-sm"} w-full`}>
                    {recursive ? (
                      <FolderSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--brand-accent)]" />
                    ) : (
                      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
                    )}
                    <input
                      type="search"
                      value={folderSearch}
                      onChange={(e) => setFolderSearch(e.target.value)}
                      placeholder={recursive
                        ? `Search ${data.assets.length} videos in this folder & subfolders…`
                        : `Search ${data.assets.length} videos in this share…`}
                      data-testid="share-folder-search"
                      /* Iter 74b — suppress browser-native `type=search`
                         clear-X; our brand-styled custom clear button lives
                         at `right-2` and avoids the double-X. */
                      style={{ WebkitAppearance: "none" }}
                      className={`[&::-webkit-search-cancel-button]:appearance-none w-full bg-zinc-950/60 rounded-full pl-9 ${recursive && folderSearch ? "pr-44" : recursive ? "pr-36" : folderSearch ? "pr-10" : "pr-4"} py-2 text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-accent)]/30 transition placeholder:text-zinc-600 ${
                        recursive ? "border border-[var(--brand-accent)]/40 focus:border-[var(--brand-accent)]/70" : "border border-zinc-800/60 focus:border-[var(--brand-accent)]/60"
                      }`}
                    />
                    {/* Iter 74b — Right-anchored controls so the X and the
                        "In folder + subs" badge never overlap. Badge sits
                        at right-10 (reserved slot ~2rem wide for the X),
                        clear button sits at right-2. The container above is
                        constrained to the same max-w as the input so the
                        absolute anchors stay inside the visible pill. */}
                    {recursive && (
                      <span
                        data-testid="share-search-scope-badge"
                        title="Search covers this folder and every subfolder inside it"
                        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-[0.2em] text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-2 py-0.5 rounded-full whitespace-nowrap hidden sm:inline-block ${folderSearch ? "right-10" : "right-3"}`}
                      >
                        In folder + subs
                      </span>
                    )}
                    {folderSearch && (
                      <button
                        type="button"
                        data-testid="share-clear-search-btn"
                        onClick={() => setFolderSearch("")}
                        title="Clear the search input"
                        className="absolute top-1/2 -translate-y-1/2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-zinc-400 hover:text-red-400 hover:bg-zinc-900/60 transition"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {folderSearch && (
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1.5 ml-1">
                      {filteredFolderAssets.length} {filteredFolderAssets.length === 1 ? "match" : "matches"}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Iter 74 — Subfolder tiles now live BELOW the search bar.
                Hidden while a search/filter is active because the recipient
                is looking for assets, not folders. */}
            {data?.kind === "folder" && (data?.subfolders?.length || 0) > 0 && !folderSearch && !tagFilter && (
              <div className="mb-6" data-testid="share-subfolder-tiles">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 flex items-center gap-2">
                  <FolderOpen className="w-3 h-3" /> Subfolders · {data.subfolders.length}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(160px,200px))] gap-3">
                  {data.subfolders.map((sf) => (
                    <button
                      key={sf.id}
                      type="button"
                      data-testid={`subfolder-tile-${sf.id}`}
                      onClick={() => setDrillFolderId(sf.id)}
                      className="group text-left border border-zinc-800/70 hover:border-[var(--brand-accent)]/60 rounded-lg px-3 py-3 bg-zinc-950/40 hover:bg-zinc-950/70 transition"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FolderOpen className="w-4 h-4 text-[var(--brand-accent)] shrink-0 group-hover:scale-110 transition-transform" />
                        <span className="text-sm text-zinc-100 font-medium truncate flex-1">{sf.name}</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                        {sf.descendant_asset_count} {sf.descendant_asset_count === 1 ? "video" : "videos"}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Strict-scope tag filter chips — only render when the share has
                tags on its DIRECT assets AND the current grid is non-empty.
                At an empty-grid state (no data OR filtered to zero), the chip
                row disappears so the client only sees the search bar. */}
            {shareTags.length > 0 && filteredFolderAssets.length > 0 && (
              <div
                data-testid="share-tag-filter-bar"
                className="flex items-center gap-2 mb-6 flex-wrap"
              >
                <TagsIcon className="w-3.5 h-3.5 text-zinc-500" />
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Filter:</span>
                <button
                  data-testid="share-tag-filter-all"
                  onClick={() => setTagFilter(null)}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border transition ${!tagFilter ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]" : "text-zinc-400 border-zinc-800 hover:border-zinc-600"}`}
                >
                  All
                </button>
                {shareTags.slice(0, 15).map((t) => (
                  <button
                    key={t.name}
                    data-testid={`share-tag-filter-${t.name}`}
                    onClick={() => setTagFilter(t.name === tagFilter ? null : t.name)}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border transition ${tagFilter === t.name ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]" : "text-zinc-400 border-zinc-800 hover:border-zinc-600"}`}
                  >
                    {t.name} {t.count}
                  </button>
                ))}
              </div>
            )}
            {filteredFolderAssets.length === 0 ? (
              (() => {
                // Iter 73b — When the recipient is just browsing (no search,
                // no tag filter) a parent folder with 0 direct videos but
                // populated subfolders, suppress the "No videos match" stub
                // because the subfolder tiles above are the real navigation.
                // Searches / tag filters that return zero DO show the stub,
                // because the recipient's intent is "find something".
                const browsing = !folderSearch && !tagFilter;
                const hasDirect = (data?.assets || []).some((a) => !data?.current_folder_id || a.folder_id === data.current_folder_id);
                if (browsing && !hasDirect && (data?.subfolders?.length || 0) > 0) return null;
                return (
                  <div className="py-10 text-center text-xs text-zinc-500 border border-dashed border-zinc-900 rounded-lg mb-8">
                    No videos match{folderSearch ? ` "${folderSearch}"` : ""}{tagFilter ? ` with tag "${tagFilter}"` : ""}.
                  </div>
                );
              })()
            ) : (
              <div
                data-testid="share-asset-grid"
                className={`grid gap-3 mb-8 ${data?.kind === "folder"
                  ? "grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(150px,180px))]"
                  : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"}`}
              >
                {filteredFolderAssets.map((a) => {
                  const aKind = a.kind || "video";
                  return (
                  <button
                    key={a.id}
                    data-testid={`share-asset-card-${a.id}`}
                    onClick={() => (aKind === "photo" ? setLightboxId(a.id) : setActiveAssetId(a.id))}
                    data-kind={aKind}
                    className={`text-left border rounded-lg overflow-hidden transition ${activeAssetId === a.id ? "border-[var(--brand-accent)]" : "border-zinc-800 hover:border-zinc-700"}`}
                  >
                    <div className="aspect-video bg-zinc-900 relative">
                      {aKind === "pdf" && !a.thumbnail_url ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-950 to-black text-zinc-500 gap-1">
                          <FileText className="w-6 h-6 text-[var(--brand-accent)]/70" strokeWidth={1.5} />
                          <span className="text-[8px] uppercase tracking-[0.2em] text-zinc-500">PDF</span>
                        </div>
                      ) : a.thumbnail_url ? (
                        <img
                          src={a.thumbnail_url}
                          alt={a.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Only videos benefit from Stream thumbnail heal.
                            if (aKind === "video") healShareThumb(slug, a.id, accessToken, e.target);
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-zinc-700">
                          {aKind === "photo" ? <ImageIcon className="w-5 h-5" /> : <Film className="w-5 h-5" />}
                        </div>
                      )}
                      {aKind === "video" && a.duration_sec > 0 && (
                        <div className="absolute bottom-1 right-1 bg-black/80 text-[9px] text-white px-1 py-0.5 rounded">{fmtDuration(a.duration_sec)}</div>
                      )}
                      {(aKind === "photo" || aKind === "pdf") && (
                        <div className="absolute bottom-1 right-1 bg-black/80 text-[9px] uppercase tracking-wider text-zinc-300 px-1 py-0.5 rounded flex items-center gap-0.5">
                          {aKind === "photo" ? <ImageIcon className="w-2.5 h-2.5" /> : <FileText className="w-2.5 h-2.5" />}
                          {aKind === "photo" ? "Photo" : "PDF"}
                        </div>
                      )}
                      {isRecentlyTagged(a.last_tagged_at) && (
                        <div
                          data-testid={`just-tagged-pip-${a.id}`}
                          title={`Tagged ${new Date(a.last_tagged_at).toLocaleString()}`}
                          className="absolute top-1 left-1 flex items-center gap-1 bg-black/70 backdrop-blur-md px-1.5 py-0.5 rounded-full"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent)] animate-pulse" />
                          <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)] font-semibold">Just tagged</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2 text-xs truncate text-zinc-200">{a.title}</div>
                  </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div ref={reviewAnchorRef} className="scroll-mt-24" />
        {activeAsset && (
          <ReviewPanel
            asset={activeAsset}
            share={data}
            slug={slug}
            accessToken={accessToken}
            watchOnly={watchOnly}
            autoplay={autoplayNext}
            onAutoplayHandled={() => setAutoplayNext(false)}
            onEnded={advanceToNext}
            onAddClipRequest={data?.allow_clip_requests ? (clip) => {
              // Iter 75 — ReviewPanel captures In/Out + note then pushes
              // the clip up here so the tray is a single source of truth.
              // Iter 76 — Clip Request flow is scoped to folder shares only;
              // asset/collection review links keep their untouched UX.
              const next = [...clipTray, clip];
              saveTray(slug, next);
              setClipTray(next);
            } : null}
          />
        )}
      </main>

      {/* Iter 75 — Clip Request tray (public share only). Fixed bottom-
          right pill expands into a submit drawer. Admin UI never includes
          this component.
          Iter 76 — Only mounted on folder shares. Asset-review and
          collection shares intentionally omit the tray entirely. */}
      {lightboxId && data?.assets.some((a) => a.id === lightboxId) && (
        <PhotoLightbox
          asset={data.assets.find((a) => a.id === lightboxId)}
          share={data}
          slug={slug}
          accessToken={accessToken}
          onClose={() => setLightboxId(null)}
          onReview={watchOnly ? null : () => {
            setActiveAssetId(lightboxId);
            setLightboxId(null);
            setTimeout(() => reviewAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
          }}
        />
      )}

      {data?.allow_clip_requests && (
        <ClipRequestTray slug={slug} clips={clipTray} onChange={setClipTray} />
      )}

      <footer className="border-t border-zinc-900/60 mt-12 py-6 text-center text-[10px] uppercase tracking-[0.25em] text-zinc-600">
        {watchOnly ? "Whiplash Film & Creative" : "Whiplash Film & Creative · Secure client review"}
      </footer>
      {/* Iter 45 — dark-themed toasts for download feedback (matches admin app) */}
      <Toaster theme="dark" position="bottom-right" richColors />
    </div>
  );
}

// Photo lightbox — overlays the grid (scroll position preserved); closes on
// backdrop click or Escape. Download respects share.allow_download.
function PhotoLightbox({ asset, share, slug, accessToken, onClose, onReview }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      data-testid="photo-lightbox"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-150"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        data-testid="photo-lightbox-content"
        className="w-full max-w-6xl max-h-full flex flex-col bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-900 flex-wrap">
          <ImageIcon className="w-4 h-4 text-[var(--brand-accent)] shrink-0" />
          <div data-testid="photo-lightbox-title" className="flex-1 min-w-0 text-sm text-zinc-100 truncate" title={asset.title}>
            {asset.title}
          </div>
          <div className="order-3 sm:order-2 w-full sm:w-auto flex items-center gap-2">
            {onReview && (
              <button
                data-testid="photo-lightbox-review-btn"
                onClick={onReview}
                className="flex items-center gap-1.5 bg-[var(--brand-accent)] text-black font-semibold rounded-full px-3 py-1.5 text-xs hover:opacity-90 transition-opacity"
              >
                <MessageSquare className="w-3 h-3" /> Comment / Review
              </button>
            )}
            {share.allow_download && <DownloadMenu asset={asset} slug={slug} accessToken={accessToken} />}
          </div>
          <button
            data-testid="photo-lightbox-close-btn"
            onClick={onClose}
            aria-label="Close"
            className="order-2 sm:order-3 p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center bg-black rounded-b-xl p-2">
          <img
            data-testid="photo-lightbox-image"
            src={asset.cdn_url}
            alt={asset.title}
            className="max-w-full max-h-[calc(100vh-9rem)] object-contain"
          />
        </div>
      </div>
    </div>
  );
}

function CenterScreen({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] grain-overlay p-6">
      {children}
    </div>
  );
}

// Iter 55 — Color palette swatches for the drawing toolbar. Three dots
// (Orange / Red / Green) that apply to both Pen and Highlighter. The active
// swatch shows a subtle ring so keyboard users can see focus/selection.
function ColorSwatches({ activeColor, onPick }) {
  const swatches = [
    { key: "orange", label: "Orange" },
    { key: "red",    label: "Red" },
    { key: "green",  label: "Green" },
  ];
  return (
    <div className="inline-flex items-center gap-1" data-testid="drawing-color-palette" role="radiogroup" aria-label="Stroke color">
      {swatches.map((s) => (
        <button
          key={s.key}
          type="button"
          role="radio"
          aria-checked={activeColor === s.key}
          data-testid={`drawing-color-${s.key}`}
          data-active={activeColor === s.key ? "true" : "false"}
          onClick={() => onPick(s.key)}
          title={s.label}
          className={`w-4 h-4 rounded-full border transition ${activeColor === s.key
            ? "border-white ring-2 ring-white/40 scale-110"
            : "border-zinc-700 hover:border-zinc-400"}`}
          style={{ backgroundColor: DRAW_PALETTE[s.key] }}
        />
      ))}
    </div>
  );
}

function ReviewPanel({ asset, share, slug, accessToken, watchOnly, autoplay, onAutoplayHandled, onEnded, onAddClipRequest }) {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const pdfViewerRef = useRef(null);
  const [embed, setEmbed] = useState("");
  const [comments, setComments] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("wd_share_name") || "");
  const [email, setEmail] = useState(() => localStorage.getItem("wd_share_email") || "");
  const [text, setText] = useState("");
  // Iter 48 — Anchor toggle default ON for both timestamp (video) and
  // page reference (pdf). Users can still uncheck for a plain comment.
  const [stamp, setStamp] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [approvalNote, setApprovalNote] = useState("");
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(null);
  // Iter 51 — Client-side "Show history from previous versions" toggle.
  const [showAllVersionComments, setShowAllVersionComments] = useState(false);
  // Iter 52 — Elite Annotation state. `activeTool` is null unless the user
  // has tapped "Add drawing" — then it's 'pen' or 'highlight'. `capturedStrokes`
  // are the strokes for the CURRENT comment being written; they're posted
  // with the comment and reset after submit/cancel. `thisPageOnly` narrows
  // the feedback list to the page currently visible in the viewer.
  const [activeTool, setActiveTool] = useState(null);
  // Iter 55 — Color palette state (Orange default, Red, Green). Applies to
  // both Pen and Highlighter. Stamped onto each stroke at completion time.
  const [activeColor, setActiveColor] = useState("orange");
  const [capturedStrokes, setCapturedStrokes] = useState([]);
  const [thisPageOnly, setThisPageOnly] = useState(false);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);
  const approvedByAny = comments.some((c) => c.approval === "approved");

  useEffect(() => {
    // Iter 45 — only videos have Bunny Stream embed URLs. Skip for photo/PDF.
    // Iter 47 — bug fix: still need to fetch comments for photo/PDF assets;
    // only the embed fetch is video-only.
    if ((asset.kind || "video") === "video" && asset.status === "ready") {
      axios.get(`${API}/share/${slug}/assets/${asset.id}/embed`, { params: accessToken ? { token: accessToken } : {} })
        .then(({ data }) => {
          let url = data.embed_url;
          if (autoplay) {
            // The backend signs the URL with autoplay=false. Flip it for the auto-advance flow.
            try {
              const u = new URL(url);
              u.searchParams.set("autoplay", "true");
              url = u.toString();
            } catch (e) { /* keep original */ }
            onAutoplayHandled?.();
          }
          setEmbed(url);
        })
        .catch(() => {});
    }
    if (!watchOnly) refreshComments();
    // eslint-disable-next-line
  }, [asset.id, showAllVersionComments]);

  const refreshComments = async () => {
    try {
      const params = accessToken ? { token: accessToken } : {};
      // Iter 51 — non-destructive history view. When on, we ask the backend
      // to fold in all sibling versions' comments (scoped to this share) so
      // clients see prior feedback with a V{n} badge on each row.
      if (showAllVersionComments) params.include_all_versions = "true";
      const { data } = await axios.get(`${API}/share/${slug}/assets/${asset.id}/comments`, { params });
      setComments(data);
    } catch (e) {
      // best-effort comment fetch
    }
  };

  // Iter 54 — Time-locked video annotation state. `isPaused` gates stroke
  // visibility (only rendered when the video is paused near the anchor time).
  // `capturedTimestamp` freezes the frame the current comment's strokes belong
  // to — set when the user activates Pen/Highlight on a video composer.
  // Iter 59 — `videoTimeKnown` prevents ghost drawings on Frame 1. It stays
  // false until Bunny's player fires its first timeupdate/pause event, so
  // we never render strokes against a default currentTime=0 (or a stale
  // value carried over from a previous asset swap).
  const [isPaused, setIsPaused] = useState(true);
  const [capturedTimestamp, setCapturedTimestamp] = useState(null);
  const [videoTimeKnown, setVideoTimeKnown] = useState(false);

  // Reset every time the current asset swaps. Otherwise `currentTime` (and
  // therefore the visibility filter) carries the previous asset's playhead
  // value, which is exactly the "photo → video ghost" leak the user reported.
  useEffect(() => {
    setCurrentTime(0);
    setIsPaused(true);
    setCapturedTimestamp(null);
    setVideoTimeKnown(false);
    setActiveTool(null);
    setCapturedStrokes([]);
  }, [asset?.id]);

  // Wire up player.js once the iframe is loaded — captures real-time playback position
  const onIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const player = new playerjs.Player(iframe);
      playerRef.current = player;
      player.on("ready", () => {
        player.on("timeupdate", (data) => {
          if (data && typeof data.seconds === "number") {
            setCurrentTime(data.seconds);
            // Iter 59 — flip the visibility gate the moment Bunny confirms
            // a real playhead position (kills Frame-1 ghosts).
            setVideoTimeKnown(true);
          }
        });
        // Iter 54 — subscribe to play/pause so we can (a) hide strokes while
        // playing and (b) auto-cancel draw mode if the user resumes mid-draft.
        player.on("play", () => {
          setIsPaused(false);
          setActiveTool((t) => {
            if (t) {
              toast.info("Video resumed — draft cleared");
              setCapturedStrokes([]);
              setCapturedTimestamp(null);
            }
            return null;
          });
        });
        player.on("pause", () => {
          setIsPaused(true);
          setVideoTimeKnown(true); // Iter 59 — pause also confirms real playhead
          // Iter 58 — Focus Hijack: when the user pauses via a click on the
          // Bunny iframe, the iframe keeps focus and swallows the very next
          // arrow key press. Forcefully pull focus back to the parent
          // document so our 0.04167s frame-scrub handler receives the event.
          try { window.focus(); } catch (e) {}
          try { iframeRef.current?.blur(); } catch (e) {}
          try {
            const ae = document.activeElement;
            if (ae && ae !== document.body) ae.blur?.();
          } catch (e) {}
        });
        if (onEnded) player.on("ended", () => onEnded());
      });
    } catch (e) {
      // player.js init failed — timestamp will fall back to manual entry
    }
  };

  const seekTo = (t) => {
    if (playerRef.current && typeof t === "number") {
      try {
        playerRef.current.setCurrentTime(t);
        playerRef.current.play();
        // Iter 60 — Do NOT window-scroll. The Bunny iframe is already in the
        // main viewer next to the feedback sidebar. Only scroll if the iframe
        // is fully off-screen, and then use `block:'nearest'` so the parent
        // header stays 100% visible (mirrors bounding-rect-delta math).
        maybeNudgeIntoView(iframeRef.current);
        return;
      } catch (e) { /* fall through */ }
    }
    // Fallback: reload iframe with t param
    const iframe = iframeRef.current;
    if (!iframe) return;
    const url = new URL(iframe.src);
    url.searchParams.set("autoplay", "true");
    url.searchParams.set("t", Math.floor(t));
    iframe.src = url.toString();
    maybeNudgeIntoView(iframe);
  };

  // Iter 54 — seek + pause (for Drawing chip clicks that reveal an annotation).
  const seekAndPause = (t) => {
    if (!playerRef.current || typeof t !== "number") return;
    try {
      playerRef.current.setCurrentTime(t);
      playerRef.current.pause();
    } catch (e) { /* best-effort */ }
  };

  // Iter 54 — Activate a video drawing tool. Pauses the player, captures the
  // current frame's timestamp so all subsequent strokes anchor to it, and
  // toggles the active tool. Idempotent — clicking the active tool turns it off.
  // Iter 56 — Blur the Bunny iframe so ArrowLeft/Right keys don't hit Bunny's
  // internal 5-second seek handler. The iframe is cross-origin, so parent-side
  // preventDefault can't cancel its internal listeners; the only way to
  // reliably intercept keys is to move focus off the iframe.
  const activateVideoTool = (tool) => {
    if (activeTool === tool) {
      setActiveTool(null);
      return;
    }
    try { playerRef.current?.pause(); } catch (e) {}
    try {
      playerRef.current?.getCurrentTime?.((secs) => {
        if (typeof secs === "number") setCapturedTimestamp(secs);
      });
    } catch (e) {}
    // Fall back to the last known currentTime if player.js can't respond.
    setCapturedTimestamp((prev) => prev ?? currentTime);
    setActiveTool(tool);
    // Move focus off the Bunny iframe so frame-scrubbing arrow keys aren't
    // captured by Bunny's internal ±5s handler.
    try { iframeRef.current?.blur(); } catch (e) {}
    try {
      if (document.activeElement === iframeRef.current) {
        (document.activeElement).blur?.();
      }
    } catch (e) {}
  };

  // Iter 54 — Frame-by-frame scrubbing (24fps ≈ 41.7ms/frame). Only active
  // when the video is paused AND focus isn't on an input. Arrow keys step
  // one frame in either direction so reviewers can nail the annotation frame.
  // Iter 56 — Use `capture` phase + `stopImmediatePropagation` so we win over
  // any other window-level listeners. Also blur the Bunny iframe on every
  // arrow press — the iframe is cross-origin, so preventDefault on the parent
  // window CANNOT cancel Bunny's own ±5s seek. Moving focus off is the only
  // Iter 56 — Blur the Bunny iframe so ArrowLeft/Right keys don't hit Bunny's
  // internal 5-second seek handler. The iframe is cross-origin, so parent-side
  // preventDefault can't cancel its internal listeners; the only way to
  // reliably keep our 24fps frame step from being overridden.
  // Iter 57 — Frame step pinned to exactly 0.04167s (24fps math). 24 presses
  // ≈ 1.00008s of video, matching the user's frame-perfect editorial cadence.
  const FRAME_STEP = 0.04167;
  useEffect(() => {
    if (asset?.kind !== "video") return;
    const onKey = (ev) => {
      if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
      // Don't steal from text inputs / textareas.
      const t = ev.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (!isPaused) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      // Belt-and-braces: even if this fires, Bunny's iframe key handler runs
      // inside its own document. Blur the iframe so the very NEXT keypress
      // arrives here first.
      try { iframeRef.current?.blur(); } catch (e) {}
      try {
        const ae = document.activeElement;
        if (ae === iframeRef.current) ae.blur?.();
      } catch (e) {}
      const delta = ev.key === "ArrowRight" ? FRAME_STEP : -FRAME_STEP;
      const next = Math.max(0, currentTime + delta);
      try {
        playerRef.current?.setCurrentTime(next);
        playerRef.current?.pause();
      } catch (e) {}
      setCurrentTime(next);
      if (activeTool) setCapturedTimestamp(next);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [asset, isPaused, currentTime, activeTool]);

  // Iter 55 — Drawing hotkeys (P/H/U/C). Only fire while a drawing tool is
  // active so they never interfere with the comment textarea. We also bail
  // out if the event target is any editable field, and if a modifier key is
  // held (so Cmd+C copy still works).
  useEffect(() => {
    if (!activeTool) return;
    const onDrawKey = (ev) => {
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const t = ev.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      const k = (ev.key || "").toLowerCase();
      if (k === "p") {
        ev.preventDefault();
        if (asset.kind === "video") activateVideoTool("pen");
        else setActiveTool("pen");
      } else if (k === "h") {
        ev.preventDefault();
        if (asset.kind === "video") activateVideoTool("highlight");
        else setActiveTool("highlight");
      } else if (k === "u") {
        ev.preventDefault();
        setCapturedStrokes((prev) => prev.slice(0, -1));
      } else if (k === "c") {
        ev.preventDefault();
        setCapturedStrokes([]);
      }
    };
    window.addEventListener("keydown", onDrawKey);
    return () => window.removeEventListener("keydown", onDrawKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, asset]);

  const [replyingTo, setReplyingTo] = useState(null);  // parent comment object when replying

  const submitComment = async (e) => {
    e.preventDefault();
    if (!name || !email || !text.trim()) return;
    setSubmitting(true);
    localStorage.setItem("wd_share_name", name);
    localStorage.setItem("wd_share_email", email);
    try {
      const { data } = await axios.post(`${API}/share/${slug}/assets/${asset.id}/comments`, {
        commenter_name: name,
        commenter_email: email,
        text: text.trim(),
        // Iter 47 — kind-branched anchor. Only one of timestamp_sec / page_number
        // is ever non-null. Replies inherit context and never carry an anchor.
        timestamp_sec: replyingTo ? null : (stamp && asset.kind === "video" ? currentTime : null),
        // Iter 47 / 54 — kind-branched anchor. Video comments prefer the
        // captured-draw timestamp when drawings exist so the anchor matches
        // the frame the user drew on; otherwise use the current playhead
        // when the timestamp toggle is on.
        timestamp_sec: replyingTo
          ? null
          : (stamp && asset.kind === "video"
              ? (capturedTimestamp !== null && capturedStrokes.length > 0 ? capturedTimestamp : currentTime)
              : null),
        // Iter 53 — Photos have no page anchor. PDFs still require it.
        page_number: replyingTo ? null : (stamp && asset.kind === "pdf" ? currentPage : null),
        parent_comment_id: replyingTo?.id || null,
        // Iter 52 / 53 / 54 — Attach drawing strokes if any were captured.
        // Server accepts on PDFs (page_number), photos (no anchor), and videos
        // (timestamp_sec anchor).
        drawing: (!replyingTo && (asset.kind === "pdf" || asset.kind === "photo" || asset.kind === "video") && capturedStrokes.length > 0)
          ? { strokes: capturedStrokes }
          : null,
      }, { params: accessToken ? { token: accessToken } : {} });
      saveDeleteToken(slug, data.id, data.delete_token);
      setText("");
      setReplyingTo(null);
      setCapturedStrokes([]);
      setActiveTool(null);
      setCapturedTimestamp(null);
      await refreshComments();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const submitApproval = async (approval) => {
    if (!name || !email) {
      alert("Please enter your name and email below before approving.");
      return;
    }
    localStorage.setItem("wd_share_name", name);
    localStorage.setItem("wd_share_email", email);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/share/${slug}/assets/${asset.id}/approval`, {
        commenter_name: name,
        commenter_email: email,
        approval,
        note: approvalNote,
      }, { params: accessToken ? { token: accessToken } : {} });
      saveDeleteToken(slug, data.id, data.delete_token);
      setApprovalNote("");
      setShowApprovalConfirm(null);
      await refreshComments();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteComment = async (commentId) => {
    const delTok = getDeleteToken(slug, commentId);
    if (!delTok) return;
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/share/${slug}/assets/${asset.id}/comments/${commentId}`, {
        params: { delete_token: delTok, ...(accessToken ? { token: accessToken } : {}) },
      });
      removeDeleteToken(slug, commentId);
      await refreshComments();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not delete comment");
    }
  };

  return (
    <div className={watchOnly ? "space-y-6" : "grid grid-cols-1 md:grid-cols-3 gap-6 min-w-0"}>
      <div className={watchOnly ? "space-y-4" : "md:col-span-2 space-y-4 min-w-0"}>
        <div className={`bg-black border border-zinc-800/60 rounded-xl overflow-hidden ${asset.kind === "video" ? "aspect-video" : ""}`}>
          {asset.kind === "photo" ? (
            // Iter 53 — Photo wrapper is `relative` so the annotation layer
            // can absolutely position over the <img>. The wrapper hugs the
            // rendered image (object-contain keeps its natural aspect); the
            // annotation SVG matches those bounds exactly via inset:0.
            <div className="relative w-full flex items-center justify-center bg-black">
              <img
                data-testid="share-photo-viewer"
                src={asset.cdn_url}
                alt={asset.title}
                className="w-full max-h-[70vh] object-contain block bg-black"
              />
              <PdfAnnotationLayer
                pageNumber={1}
                strokes={comments
                  .filter((c) => c.drawing?.strokes?.length)
                  .flatMap((c) => (c.drawing.strokes || []).map((s, i) => ({
                    tool: s.tool, color: s.color, points: s.points, commentId: c.id, id: `${c.id}-${i}`,
                  })))
                  .concat(capturedStrokes.map((s, i) => ({ ...s, id: `draft-${i}` })))}
                interactive={!!activeTool}
                activeTool={activeTool || "pen"}
                activeColor={activeColor}
                onStrokeComplete={(stroke) => setCapturedStrokes((prev) => [...prev, stroke])}
                highlightedCommentIds={highlightedCommentId ? [highlightedCommentId] : []}
              />
            </div>
          ) : asset.kind === "pdf" ? (
            <div className="w-full min-h-[600px] h-[75vh]">
              <PdfViewer
                ref={pdfViewerRef}
                url={asset.cdn_url}
                title={asset.title}
                onPageChange={setCurrentPage}
                renderPageOverlay={(pageNum, pageWidthPx) => {
                  // Playback strokes for THIS page across all visible comments
                  // (respecting the History toggle). Each stroke carries the
                  // commentId so we can glow the right one when clicked.
                  const strokesForPage = comments
                    .filter((c) => c.drawing?.strokes?.length && c.page_number === pageNum)
                    .flatMap((c) => (c.drawing.strokes || []).map((s, i) => ({
                      tool: s.tool,
                      color: s.color,
                      points: s.points,
                      commentId: c.id,
                      id: `${c.id}-${i}`,
                    })));
                  const interactive = !!activeTool && currentPage === pageNum && asset.kind === "pdf";
                  return (
                    <PdfAnnotationLayer
                      pageNumber={pageNum}
                      pageWidth={pageWidthPx}
                      strokes={strokesForPage.concat(
                        // Also render the in-progress captured strokes for the
                        // current comment (before submit).
                        currentPage === pageNum
                          ? capturedStrokes.map((s, i) => ({ ...s, id: `draft-${i}` }))
                          : []
                      )}
                      interactive={interactive}
                      activeTool={activeTool || "pen"}
                      activeColor={activeColor}
                      onStrokeComplete={(stroke) => {
                        setCapturedStrokes((prev) => [...prev, stroke]);
                      }}
                      highlightedCommentIds={highlightedCommentId ? [highlightedCommentId] : []}
                    />
                  );
                }}
              />
            </div>
          ) : asset.status === "ready" && embed ? (
            // Iter 54 — Video wrapper is `relative` so the annotation overlay
            // can absolutely position over the iframe. The overlay itself
            // stays `pointer-events: none` unless a drawing tool is active,
            // so scrubbing/play/pause still works through the iframe.
            <div className="relative w-full h-full">
              <iframe
                ref={iframeRef}
                src={embed}
                onLoad={onIframeLoad}
                className="w-full h-full"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                title={asset.title}
              />
              <PdfAnnotationLayer
                pageNumber={1}
                strokes={comments
                  // Iter 54 — time-lock: only render a video comment's strokes
                  // when the player is paused within ±0.5s of its anchor.
                  // Iter 59 — additionally gate on videoTimeKnown so we never
                  // paint against a default currentTime=0 before Bunny has
                  // reported a real playhead position (kills Frame-1 ghosts).
                  .filter((c) => c.drawing?.strokes?.length && typeof c.timestamp_sec === "number"
                             && videoTimeKnown && isPaused
                             && Math.abs(c.timestamp_sec - currentTime) < 0.5)
                  .flatMap((c) => (c.drawing.strokes || []).map((s, i) => ({
                    tool: s.tool, color: s.color, points: s.points, commentId: c.id, id: `${c.id}-${i}`,
                  })))
                  .concat(activeTool && capturedTimestamp !== null
                    ? capturedStrokes.map((s, i) => ({ ...s, id: `draft-${i}` }))
                    : [])}
                interactive={!!activeTool}
                activeTool={activeTool || "pen"}
                activeColor={activeColor}
                onStrokeComplete={(stroke) => setCapturedStrokes((prev) => [...prev, stroke])}
                highlightedCommentIds={highlightedCommentId ? [highlightedCommentId] : []}
              />
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
              <Loader2 className="w-6 h-6 animate-spin mb-2 text-[var(--brand-accent)]" />
              <div className="text-xs uppercase tracking-wider">Video processing — check back in a moment</div>
            </div>
          )}
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="font-heading text-xl font-semibold tracking-tight truncate">{asset.title}</h2>
            {asset.last_replaced_at && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-accent)] mt-1">
                Updated {new Date(asset.last_replaced_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}
              </div>
            )}
            {asset.description && <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{asset.description}</p>}
          </div>
          {(share.allow_download || approvedByAny) && (
            <DownloadMenu asset={asset} slug={slug} accessToken={accessToken} />
          )}
        </div>

        {share.allow_approval && (
          <div className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Your decision</div>
            {showApprovalConfirm ? (
              <div className="space-y-3">
                <textarea
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  placeholder={showApprovalConfirm === "approved" ? "Optional note…" : "Briefly describe the changes you'd like…"}
                  rows={2}
                  className="w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => submitApproval(showApprovalConfirm)}
                    disabled={submitting}
                    className="bg-[var(--brand-accent)] text-black font-semibold rounded-md px-4 py-2 text-xs hover:bg-[var(--brand-accent)]/90 transition disabled:opacity-60"
                  >
                    Confirm {showApprovalConfirm === "approved" ? "approval" : showApprovalConfirm === "rejected" ? "rejection" : "change request"}
                  </button>
                  <button onClick={() => setShowApprovalConfirm(null)} className="text-xs text-zinc-500 hover:text-zinc-300 px-2">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <ApproveBtn icon={ThumbsUp} label="Approve" color="emerald" onClick={() => setShowApprovalConfirm("approved")} />
                <ApproveBtn icon={RotateCcw} label="Request changes" color="amber" onClick={() => setShowApprovalConfirm("changes-requested")} />
                <ApproveBtn icon={ThumbsDown} label="Reject" color="red" onClick={() => setShowApprovalConfirm("rejected")} />
              </div>
            )}
          </div>
        )}
        {/* Iter 75 — Clip Request panel. Only renders for video assets on
            public share pages; the authenticated DAM never passes
            onAddClipRequest. Captures In/Out via playerRef.getCurrentTime
            (second-level precision rounded to 0.1s to match the
            frame-scrub granularity in the authenticated player). */}
        {onAddClipRequest && asset.kind === "video" && (
          <ClipRequestMarker asset={asset} playerRef={playerRef} onAdd={onAddClipRequest} />
        )}
      </div>

      {!watchOnly && (
      <aside className="space-y-4">
        <div className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <MessageSquare className="w-4 h-4 text-[var(--brand-accent)]" />
              <h3 className="font-heading font-semibold">Feedback</h3>
              {/* Iter 51 — Client-side inline "History" pill. Non-destructive
                  view toggle that folds in every sibling version's comments
                  (still scoped to this share) so reviewers can see prior
                  feedback with a V{n} badge on each. Hidden when the group
                  has only one version. */}
              {(asset.sibling_version_count || 1) > 1 && (
                <button
                  type="button"
                  data-testid="share-show-history-toggle"
                  onClick={() => setShowAllVersionComments((v) => !v)}
                  className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition uppercase tracking-wider ${showAllVersionComments
                    ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]"
                    : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/50 hover:text-[var(--brand-accent)]"}`}
                  title={showAllVersionComments ? "Hide comments from other versions" : "Show comments from all versions"}
                >
                  <Layers className="w-2.5 h-2.5" /> History
                </button>
              )}
              {/* Iter 52 — "This Page Only" — filter feedback to comments
                  anchored to the currently visible PDF page. Only meaningful
                  on PDFs; hidden otherwise. */}
              {asset.kind === "pdf" && (
                <button
                  type="button"
                  data-testid="share-this-page-only-toggle"
                  onClick={() => setThisPageOnly((v) => !v)}
                  className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition uppercase tracking-wider ${thisPageOnly
                    ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]"
                    : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/50 hover:text-[var(--brand-accent)]"}`}
                  title={thisPageOnly ? "Show feedback from all pages" : "Show only feedback on this page"}
                >
                  <FileText className="w-2.5 h-2.5" /> This page
                </button>
              )}
              {/* Iter 54 — "This Time Only" — mirror of "This page" for videos.
                  Filters top-level comments to ones anchored within ±2s of
                  the current playhead so reviewers can focus on this moment. */}
              {asset.kind === "video" && (
                <button
                  type="button"
                  data-testid="share-this-time-only-toggle"
                  onClick={() => setThisPageOnly((v) => !v)}
                  className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition uppercase tracking-wider ${thisPageOnly
                    ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]"
                    : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/50 hover:text-[var(--brand-accent)]"}`}
                  title={thisPageOnly ? "Show feedback from the whole video" : "Show only feedback near this moment (±2s)"}
                >
                  <Clock className="w-2.5 h-2.5" /> This time
                </button>
              )}
            </div>
            {/* Iter 52 / 54 — Comment counter respects both the "This page only"
                (PDF) and "This time only" (video, same state flag) filters. */}
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{
              thisPageOnly && asset.kind === "pdf"
                ? comments.filter((c) => !c.parent_comment_id && c.page_number === currentPage).length
                : thisPageOnly && asset.kind === "video"
                  ? comments.filter((c) => !c.parent_comment_id && typeof c.timestamp_sec === "number" && Math.abs(c.timestamp_sec - currentTime) < 2).length
                  : comments.length
            }</div>
          </div>

          <div className="max-h-[420px] overflow-y-auto space-y-3 mb-4 pr-1">
            {(() => {
              // Iter 52 / 54 — visible top-level comment list. History toggle is
              // handled server-side; the client filters narrow further:
              //   • PDF   — "This page only"  → c.page_number === currentPage
              //   • Video — "This time only"  → |c.timestamp_sec − currentTime| < 2s
              const topLevel = comments.filter((c) => !c.parent_comment_id);
              let visible = topLevel;
              if (thisPageOnly && asset.kind === "pdf") {
                visible = topLevel.filter((c) => c.page_number === currentPage);
              } else if (thisPageOnly && asset.kind === "video") {
                visible = topLevel.filter((c) => typeof c.timestamp_sec === "number" && Math.abs(c.timestamp_sec - currentTime) < 2);
              }
              if (visible.length === 0) {
                return (
                  <div className="text-xs text-zinc-500 text-center py-6" data-testid="share-comments-empty">
                    {thisPageOnly && asset.kind === "pdf" ? `No feedback on page ${currentPage}.`
                      : thisPageOnly && asset.kind === "video" ? `No feedback near ${fmtDuration(currentTime)}.`
                      : "Be the first to leave feedback."}
                  </div>
                );
              }
              return visible.map((c) => {
                const replies = comments.filter((r) => r.parent_comment_id === c.id);
                return (
                  <div key={c.id}>
                    <CommentItem
                      c={c}
                      onSeek={seekTo}
                      onSeekAndPause={seekAndPause}
                      onJumpToPage={(p) => pdfViewerRef.current?.scrollToPage(p)}
                      canDelete={!!getDeleteToken(slug, c.id)}
                      onDelete={() => deleteComment(c.id)}
                      activeVersionNumber={asset.version_number}
                      onHighlight={() => {
                        // Brief 2s glow on this comment's stroke(s).
                        setHighlightedCommentId(c.id);
                        setTimeout(() => setHighlightedCommentId((cur) => (cur === c.id ? null : cur)), 2000);
                      }}
                      onReply={share.allow_comments ? () => {
                        setReplyingTo(c);
                        document.querySelector('[data-testid="share-comment-textarea"]')?.focus();
                      } : null}
                    />
                    {replies.length > 0 && (
                      <div className="mt-2 pl-6 border-l-2 border-zinc-800/60 space-y-2">
                        {replies.map((r) => (
                          <CommentItem
                            key={r.id}
                            c={r}
                            reply
                            onSeek={seekTo}
                            onSeekAndPause={seekAndPause}
                            onJumpToPage={(p) => pdfViewerRef.current?.scrollToPage(p)}
                            canDelete={!!getDeleteToken(slug, r.id)}
                            activeVersionNumber={asset.version_number}
                            onDelete={() => deleteComment(r.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {share.allow_comments && (
            <form onSubmit={submitComment} className="space-y-2 border-t border-zinc-800/60 pt-4">
              {replyingTo && (
                <div className="flex items-center justify-between gap-2 bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 rounded px-2.5 py-1.5 text-[11px] text-[var(--brand-accent)]">
                  <span className="truncate">Replying to <strong>{replyingTo.commenter_name}</strong></span>
                  <button
                    type="button"
                    onClick={() => setReplyingTo(null)}
                    data-testid="share-reply-cancel"
                    className="text-zinc-400 hover:text-zinc-100"
                    title="Cancel reply"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name" className="bg-black/40 border border-zinc-800 rounded px-2.5 py-1.5 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" placeholder="Email" className="bg-black/40 border border-zinc-800 rounded px-2.5 py-1.5 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60" />
              </div>
              <textarea value={text} onChange={(e) => setText(e.target.value)} required rows={2} data-testid="share-comment-textarea" placeholder={replyingTo ? `Reply to ${replyingTo.commenter_name}…` : "Leave a comment…"} className="w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60 resize-none" />
              {/* Iter 52 / 53 — Drawing tools row. Enabled for PDFs (page-anchored)
                  and photos (no anchor). Video shows the chips greyed out with a
                  "coming soon" tooltip so users see the tool ladder. Replies
                  never carry drawings. */}
              {!replyingTo && (asset.kind === "pdf" || asset.kind === "photo") && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="drawing-toolbar">
                  <button
                    type="button"
                    data-testid="drawing-tool-pen"
                    onClick={() => setActiveTool((t) => (t === "pen" ? null : "pen"))}
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border transition uppercase tracking-wider ${activeTool === "pen"
                      ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]"
                      : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/50 hover:text-[var(--brand-accent)]"}`}
                    title="Freehand pen (P)"
                  >
                    <Pencil className="w-2.5 h-2.5" /> Pen
                  </button>
                  <button
                    type="button"
                    data-testid="drawing-tool-highlight"
                    onClick={() => setActiveTool((t) => (t === "highlight" ? null : "highlight"))}
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border transition uppercase tracking-wider ${activeTool === "highlight"
                      ? "bg-yellow-400 text-black border-yellow-400"
                      : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-yellow-400/60 hover:text-yellow-300"}`}
                    title="Translucent highlighter (H)"
                  >
                    <Highlighter className="w-2.5 h-2.5" /> Highlight
                  </button>
                  {activeTool && (
                    <ColorSwatches activeColor={activeColor} onPick={setActiveColor} />
                  )}
                  {capturedStrokes.length > 0 && (
                    <>
                      <button
                        type="button"
                        data-testid="drawing-undo"
                        onClick={() => setCapturedStrokes((prev) => prev.slice(0, -1))}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-200 uppercase tracking-wider"
                        title="Undo last stroke (U)"
                      >
                        <Undo2 className="w-2.5 h-2.5" /> Undo
                      </button>
                      <button
                        type="button"
                        data-testid="drawing-clear"
                        onClick={() => setCapturedStrokes([])}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-red-400 uppercase tracking-wider"
                        title="Clear all strokes (C)"
                      >
                        <Trash2 className="w-2.5 h-2.5" /> Clear
                      </button>
                      <span className="text-[10px] text-zinc-500 ml-1">{capturedStrokes.length} stroke{capturedStrokes.length !== 1 ? "s" : ""}</span>
                    </>
                  )}
                  {activeTool && (
                    <span className="text-[10px] text-[var(--brand-accent)] italic ml-auto">
                      {asset.kind === "pdf" ? "Draw on the page to annotate" : "Draw on the photo to annotate"}
                    </span>
                  )}
                </div>
              )}
              {/* Iter 54 — Real drawing toolbar for videos. Same UX as PDF/photo
                  but with a "Locked at 0:XX.XX" chip so users know which frame
                  the strokes anchor to. */}
              {!replyingTo && asset.kind === "video" && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="drawing-toolbar">
                  <button
                    type="button"
                    data-testid="drawing-tool-pen"
                    onClick={() => activateVideoTool("pen")}
                    disabled={asset.status !== "ready"}
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border transition uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed ${activeTool === "pen"
                      ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]"
                      : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/50 hover:text-[var(--brand-accent)]"}`}
                    title="Freehand pen (P). Pauses the video and locks to this frame."
                  >
                    <Pencil className="w-2.5 h-2.5" /> Pen
                  </button>
                  <button
                    type="button"
                    data-testid="drawing-tool-highlight"
                    onClick={() => activateVideoTool("highlight")}
                    disabled={asset.status !== "ready"}
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border transition uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed ${activeTool === "highlight"
                      ? "bg-yellow-400 text-black border-yellow-400"
                      : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-yellow-400/60 hover:text-yellow-300"}`}
                    title="Translucent highlighter (H)"
                  >
                    <Highlighter className="w-2.5 h-2.5" /> Highlight
                  </button>
                  {activeTool && (
                    <ColorSwatches activeColor={activeColor} onPick={setActiveColor} />
                  )}
                  {activeTool && capturedTimestamp !== null && (
                    <span
                      data-testid="drawing-locked-chip"
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border font-mono uppercase tracking-wider"
                      style={{
                        borderColor: `${DRAW_PALETTE[activeColor]}99`,
                        backgroundColor: `${DRAW_PALETTE[activeColor]}1a`,
                        color: DRAW_PALETTE[activeColor],
                      }}
                      title="All strokes anchor to this frame"
                    >
                      <span
                        data-testid="drawing-locked-color-dot"
                        aria-hidden="true"
                        className="inline-block w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: DRAW_PALETTE[activeColor] }}
                      />
                      <Clock className="w-2.5 h-2.5" /> Locked at {fmtDuration(capturedTimestamp)}
                    </span>
                  )}
                  {capturedStrokes.length > 0 && (
                    <>
                      <button type="button" data-testid="drawing-undo"
                        onClick={() => setCapturedStrokes((prev) => prev.slice(0, -1))}
                        title="Undo last stroke (U)"
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-200 uppercase tracking-wider">
                        <Undo2 className="w-2.5 h-2.5" /> Undo
                      </button>
                      <button type="button" data-testid="drawing-clear"
                        onClick={() => setCapturedStrokes([])}
                        title="Clear all strokes (C)"
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-red-400 uppercase tracking-wider">
                        <Trash2 className="w-2.5 h-2.5" /> Clear
                      </button>
                      <span className="text-[10px] text-zinc-500 ml-1">{capturedStrokes.length} stroke{capturedStrokes.length !== 1 ? "s" : ""}</span>
                    </>
                  )}
                  {activeTool && (
                    <span className="text-[10px] text-[var(--brand-accent)] italic ml-auto">← → to nudge one frame</span>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                {/* Iter 47 — context-aware anchor toggle. Photos have no anchor;
                    videos get the timestamp toggle; PDFs get the page toggle. */}
                {!replyingTo && asset.kind === "video" ? (
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 cursor-pointer" data-testid="anchor-toggle-timestamp">
                    <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} className="accent-[var(--brand-accent)]" />
                    <Clock className="w-3 h-3" /> Add timestamp ({fmtDuration(currentTime)})
                  </label>
                ) : !replyingTo && asset.kind === "pdf" ? (
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 cursor-pointer" data-testid="anchor-toggle-page">
                    <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} className="accent-[var(--brand-accent)]" />
                    <FileText className="w-3 h-3" /> Add page reference (Page {currentPage})
                  </label>
                ) : <span />}
                <button type="submit" disabled={submitting} data-testid="share-comment-send" className="bg-[var(--brand-accent)] text-black font-semibold rounded-full px-3 py-1.5 text-xs hover:bg-[var(--brand-accent)]/90 transition flex items-center gap-1 disabled:opacity-60">
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  {replyingTo ? "Send reply" : "Send"}
                </button>
              </div>
            </form>
          )}
        </div>
      </aside>
      )}
    </div>
  );
}

function CommentItem({ c, onSeek, onSeekAndPause, onJumpToPage, canDelete, onDelete, onReply, activeVersionNumber, onHighlight, reply = false }) {
  const approvalLabel = {
    approved: { text: "Approved", color: "text-emerald-400 border-emerald-900/50 bg-emerald-950/30", icon: ThumbsUp },
    rejected: { text: "Rejected", color: "text-red-400 border-red-900/50 bg-red-950/30", icon: ThumbsDown },
    "changes-requested": { text: "Changes requested", color: "text-amber-400 border-amber-900/50 bg-amber-950/30", icon: RotateCcw },
  }[c.approval];

  return (
    <div className={`group ${reply ? "text-xs" : "text-sm"}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`${reply ? "w-5 h-5 text-[9px]" : "w-6 h-6 text-[10px]"} rounded-full flex items-center justify-center font-medium shrink-0 ${c.is_admin_reply ? "bg-[var(--brand-accent)]/20 text-[var(--brand-accent)]" : "bg-zinc-800 text-zinc-300"}`}>
          {c.commenter_name?.[0]?.toUpperCase()}
        </div>
        <div className="text-xs text-zinc-300 font-medium truncate">{c.commenter_name}</div>
        {c.is_admin_reply && (
          <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)]/80 bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-1.5 py-0.5 rounded-full">Team</span>
        )}
        {c.timestamp_sec !== null && c.timestamp_sec !== undefined && (
          <button
            onClick={() => onSeek?.(c.timestamp_sec)}
            data-testid={`comment-anchor-ts-${c.id}`}
            className="inline-flex items-center gap-1 text-[10px] text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 hover:bg-[var(--brand-accent)]/20 hover:border-[var(--brand-accent)]/60 transition rounded-full px-1.5 py-0.5 font-mono"
            title="Jump to this moment in the video"
          >
            <Clock className="w-2.5 h-2.5" />{fmtDuration(c.timestamp_sec)}
          </button>
        )}
        {/* Iter 47 — PDF page-anchor pill. Same visual weight as the video
            timestamp pill so proofing PDFs get a "premium" comment feel. */}
        {c.page_number !== null && c.page_number !== undefined && (
          <button
            onClick={() => onJumpToPage?.(c.page_number)}
            data-testid={`comment-anchor-page-${c.id}`}
            className="inline-flex items-center gap-1 text-[10px] text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 hover:bg-[var(--brand-accent)]/20 hover:border-[var(--brand-accent)]/60 transition rounded-full px-1.5 py-0.5 font-medium uppercase tracking-wider"
            title={`Jump to page ${c.page_number}`}
          >
            <FileText className="w-2.5 h-2.5" />Page {c.page_number}
          </button>
        )}
        {/* Iter 51 — Origin-version badge. Only renders when this comment
            came from a DIFFERENT version than the one currently in view. */}
        {c.version_number && activeVersionNumber && c.version_number !== activeVersionNumber && (
          <span
            data-testid={`share-comment-version-badge-v${c.version_number}`}
            className="inline-flex items-center gap-1 text-[10px] text-zinc-300 bg-zinc-900 border border-zinc-700 rounded-full px-1.5 py-0.5 font-semibold uppercase tracking-wider"
            title={`From version V${c.version_number}`}
          >
            V{c.version_number}
          </span>
        )}
        {/* Iter 52 / 53 / 54 — "Drawing" chip. Clicking:
            • PDF   → jumps to the anchored page + glows
            • Photo → just glows (no page anchor)
            • Video → seeks the player to the timestamp, pauses, then glows */}
        {c.drawing?.strokes?.length > 0 && (
          <button
            type="button"
            data-testid={`comment-drawing-chip-${c.id}`}
            onClick={() => {
              if (c.page_number) onJumpToPage?.(c.page_number);
              if (typeof c.timestamp_sec === "number") onSeekAndPause?.(c.timestamp_sec);
              onHighlight?.();
            }}
            className="inline-flex items-center gap-1 text-[10px] text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 hover:bg-[var(--brand-accent)]/20 hover:border-[var(--brand-accent)]/60 transition rounded-full px-1.5 py-0.5 font-medium uppercase tracking-wider"
            title="Show this annotation"
          >
            <Pencil className="w-2.5 h-2.5" /> Drawing
          </button>
        )}
        <div className="ml-auto flex items-center gap-1">
          {onReply && !reply && (
            <button
              onClick={onReply}
              data-testid={`share-reply-btn-${c.id}`}
              className="opacity-0 group-hover:opacity-100 text-[10px] text-zinc-500 hover:text-[var(--brand-accent)] transition flex items-center gap-0.5"
              title="Reply to this comment"
            >
              <MessageSquare className="w-2.5 h-2.5" /> Reply
            </button>
          )}
          {canDelete && (
            <button
              onClick={onDelete}
              className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition"
              title="Delete this comment"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      {approvalLabel && (
        <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border mb-1.5 ${approvalLabel.color}`}>
          <approvalLabel.icon className="w-2.5 h-2.5" /> {approvalLabel.text}
        </div>
      )}
      {c.text && <div className={`text-zinc-300 leading-relaxed ${reply ? "text-xs pl-7" : "text-xs pl-8 -mt-0.5"}`}>{c.text}</div>}
    </div>
  );
}

function ApproveBtn({ icon: Icon, label, color, onClick }) {
  const styles = {
    emerald: "border-emerald-900/40 text-emerald-300 hover:bg-emerald-950/30",
    amber: "border-amber-900/40 text-amber-300 hover:bg-amber-950/30",
    red: "border-red-900/40 text-red-300 hover:bg-red-950/30",
  }[color];
  return (
    <button onClick={onClick} className={`flex flex-col items-center gap-1.5 border rounded-lg py-3 transition ${styles}`}>
      <Icon className="w-4 h-4" />
      <span className="text-[11px] font-medium">{label}</span>
    </button>
  );
}

function seekPlayer(iframeRef, t) {
  // Best-effort: Bunny embed supports a query param ?t=, but for live seek we'd need the player API.
  // For now, scroll the player into view + reload with start time.
  const iframe = iframeRef.current;
  if (!iframe) return;
  const url = new URL(iframe.src);
  url.searchParams.set("autoplay", "true");
  // Bunny player uses "t" for start time in seconds
  url.searchParams.set("t", Math.floor(t));
  iframe.src = url.toString();
  // Iter 60 — bounded scroll helper: keeps header visible when Bunny embed
  // is nudged after a reload with ?t= (older browser fallback).
  maybeNudgeIntoView(iframe);
}


function DownloadMenu({ asset, slug, accessToken }) {
  const [open, setOpen] = useState(false);
  const [qualities, setQualities] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Iter 45 — per-row saving state for cross-origin blob downloads on photos.
  const [saving, setSaving] = useState({});
  // Iter 45 — kind-aware download flow. Videos keep the streaming <a href>
  // (same-origin, server sets Content-Disposition). Photos hit
  // /image-qualities and fetch-to-blob. PDFs do a single-shot blob download.
  const kind = asset.kind || "video";
  const isPhoto = kind === "photo";
  const isPdf = kind === "pdf";

  const loadQualities = async () => {
    // PDF: no menu — download immediately.
    if (isPdf) {
      const filename = buildAssetFilename({
        title: asset.title,
        resolution: null,
        ext: extForAsset(asset),
      });
      setSaving((p) => ({ ...p, pdf: true }));
      try {
        await downloadBlob(asset.cdn_url, filename);
        toast.success(`Downloaded ${filename}`);
      } catch (e) {
        toast.error(e.message || "Download failed — try again");
      } finally {
        setSaving((p) => { const n = { ...p }; delete n.pdf; return n; });
      }
      return;
    }
    if (qualities) { setOpen((p) => !p); return; }
    setBusy(true); setError("");
    try {
      const endpoint = isPhoto
        ? `${API}/share/${slug}/assets/${asset.id}/image-qualities`
        : `${API}/share/${slug}/assets/${asset.id}/download`;
      const { data } = await axios.get(endpoint,
        { params: accessToken ? { token: accessToken } : {} });
      setQualities(data.qualities || []);
      setOpen(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load qualities");
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  // Videos: same-origin streaming URL with Content-Disposition set server-side.
  // <a download href=...> triggers an instant native save.
  const buildVideoHref = (resolution) => {
    const params = new URLSearchParams({ resolution });
    if (accessToken) params.set("token", accessToken);
    return `${API}/share/${slug}/assets/${asset.id}/download.mp4?${params.toString()}`;
  };

  // Photos: fetch-to-blob so the file lands with our own filename convention.
  const downloadPhotoQuality = async (q) => {
    const key = q?.resolution || "file";
    setSaving((p) => ({ ...p, [key]: true }));
    const filename = buildAssetFilename({
      title: asset.title,
      resolution: q?.resolution,
      ext: extForAsset(asset),
    });
    try {
      await downloadBlob(q.url, filename);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.message || "Download failed — try again");
    } finally {
      setSaving((p) => { const n = { ...p }; delete n[key]; return n; });
    }
  };

  // Close on outside click — but NOT while a photo blob is being fetched, so
  // the "Saving…" spinner remains visible until the download resolves.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (Object.keys(saving).length) return;
      if (!e.target.closest("[data-download-menu]")) setOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open, saving]);

  return (
    <div data-download-menu className="relative shrink-0">
      <button
        data-testid="share-download-btn"
        onClick={loadQualities}
        disabled={busy || !!saving.pdf}
        className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 rounded-full px-3 py-1.5 text-xs hover:border-[var(--brand-accent)]/40 transition disabled:opacity-60"
      >
        {(busy || saving.pdf) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
        {saving.pdf ? "Saving…" : isPdf ? "Download PDF" : isPhoto ? "Download photo" : "Download"}
      </button>
      {open && !isPdf && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl z-30 overflow-hidden">
          {error ? (
            <div className="text-xs text-red-300 px-3 py-3">{error}</div>
          ) : (
            <>
              <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-900">
                Choose quality
              </div>
              {qualities?.length ? qualities.map((q) => {
                const isSaving = !!saving[q.resolution];
                if (isPhoto) {
                  return (
                    <button
                      key={q.resolution}
                      type="button"
                      data-testid={`share-download-${q.resolution}`}
                      onClick={() => downloadPhotoQuality(q)}
                      disabled={isSaving}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900 hover:text-[var(--brand-accent)] transition text-left disabled:opacity-70 ${q.original ? "border-b border-zinc-900 bg-zinc-900/40" : ""}`}
                    >
                      <span className="flex items-center gap-2">
                        {isSaving ? <Loader2 className="w-3 h-3 animate-spin text-[var(--brand-accent)]" /> : <Download className="w-3 h-3 opacity-60" />}
                        {isSaving ? "Saving…" : q.label}
                      </span>
                      {q.original ? (
                        <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-1.5 py-0.5 rounded">Original</span>
                      ) : q.default ? (
                        <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)]/80">Best</span>
                      ) : null}
                    </button>
                  );
                }
                // Video: keep native streaming <a href>
                return (
                  <a
                    key={q.resolution}
                    href={buildVideoHref(q.resolution)}
                    data-testid={`share-download-${q.resolution}`}
                    rel="noopener"
                    onClick={() => setTimeout(() => setOpen(false), 200)}
                    className={`flex items-center justify-between px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900 hover:text-[var(--brand-accent)] transition no-underline ${q.original ? "border-b border-zinc-900 bg-zinc-900/40" : ""}`}
                  >
                    <span className="flex items-center gap-2">
                      <Download className="w-3 h-3 opacity-60" />
                      {q.label}
                    </span>
                    {q.original ? (
                      <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-1.5 py-0.5 rounded">Original</span>
                    ) : q.default ? (
                      <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)]/80">Best</span>
                    ) : null}
                  </a>
                );
              }) : (
                <div className="text-xs text-zinc-500 px-3 py-3">No download qualities available.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
